import { jsPDF } from 'jspdf';
import { HTSClassificationData, TariffDeterminationData, TariffAddon } from 'types/chat.types';

/**
 * Metadata describing the classification being exported, mirroring the props the
 * on-screen result card receives so the PDF stays in sync with the UI.
 */
export interface ClassificationPdfMeta {
  /** Resolved product/title shown at the top of the report. */
  productName: string;
  /** Country of origin already resolved to a display name. */
  countryOfOrigin?: string;
  /** Optional classification identifier, printed in the footer for traceability. */
  classificationId?: string;
}

/** Render order + labels for the additional-program addons (kept in sync with the UI). */
const PROGRAM_ORDER = ['section_301', 'section_232', 'ieepa', 'section_122'] as const;
const PROGRAM_LABELS: Record<string, string> = {
  section_301: 'Section 301',
  section_232: 'Section 232',
  ieepa: 'IEEPA',
  section_122: 'Section 122',
};

/** Brand teal used for accents, matching the app's primary colour. */
const TEAL: [number, number, number] = [13, 148, 136];
const GRAY_DARK: [number, number, number] = [38, 38, 38];
const GRAY_MID: [number, number, number] = [82, 82, 82];
const GRAY_LIGHT: [number, number, number] = [140, 140, 140];

/**
 * Format an ad-valorem percentage for display, falling back to an em dash when
 * the value is absent. Mirrors the `fmtPct` helper used by the result card.
 */
const fmtPct = (n: number | null | undefined): string =>
  n === null || n === undefined ? '—' : `${Number(n).toFixed(Number(n) % 1 === 0 ? 0 : 2)}%`;

/**
 * Convert a confidence score (0–1) into the same banded range string shown in
 * the UI (e.g. 0.95 => "90-95%").
 */
const confidenceRange = (score?: number): string | null => {
  if (!score) return null;
  const percentage = Math.round(score * 100);
  const lowerBound = Math.max(0, percentage - 5);
  return `${lowerBound}-${percentage}%`;
};

/**
 * Stateful cursor-based PDF writer. Wraps a jsPDF instance with helpers for
 * flowing text, headings and key/value rows while automatically handling page
 * breaks and margins.
 */
class PdfWriter {
  private readonly doc: jsPDF;
  private readonly marginX = 48;
  private readonly marginTop = 56;
  private readonly marginBottom = 56;
  private readonly pageWidth: number;
  private readonly pageHeight: number;
  private y: number;

  /** Create a writer backed by a fresh A4 portrait document in points. */
  constructor() {
    this.doc = new jsPDF({ unit: 'pt', format: 'a4' });
    this.pageWidth = this.doc.internal.pageSize.getWidth();
    this.pageHeight = this.doc.internal.pageSize.getHeight();
    this.y = this.marginTop;
  }

  /** Usable content width between the left and right margins. */
  private get contentWidth(): number {
    return this.pageWidth - this.marginX * 2;
  }

  /** Add a new page if the next `needed` points would overflow the bottom margin. */
  private ensureSpace(needed: number): void {
    if (this.y + needed > this.pageHeight - this.marginBottom) {
      this.doc.addPage();
      this.y = this.marginTop;
    }
  }

  /** Move the cursor down by `amount` points. */
  spacer(amount: number): void {
    this.y += amount;
  }

  /** Draw a thin horizontal divider with surrounding breathing room. */
  divider(): void {
    this.ensureSpace(16);
    this.y += 6;
    this.doc.setDrawColor(229, 229, 229);
    this.doc.setLineWidth(0.5);
    this.doc.line(this.marginX, this.y, this.pageWidth - this.marginX, this.y);
    this.y += 14;
  }

  /**
   * Write a block of wrapped text in the given style and advance the cursor.
   * Returns nothing; the cursor is left below the rendered block.
   */
  text(
    content: string,
    opts: {
      size?: number;
      style?: 'normal' | 'bold' | 'italic';
      color?: [number, number, number];
      indent?: number;
      lineGap?: number;
    } = {},
  ): void {
    const { size = 10, style = 'normal', color = GRAY_MID, indent = 0, lineGap = 4 } = opts;
    this.doc.setFont('helvetica', style);
    this.doc.setFontSize(size);
    this.doc.setTextColor(color[0], color[1], color[2]);

    const lines = this.doc.splitTextToSize(content, this.contentWidth - indent) as string[];
    const lineHeight = size + lineGap;
    for (const line of lines) {
      this.ensureSpace(lineHeight);
      this.doc.text(line, this.marginX + indent, this.y);
      this.y += lineHeight;
    }
  }

  /** Render a section heading (small, bold) with spacing above. */
  heading(title: string): void {
    this.ensureSpace(28);
    this.y += 6;
    this.text(title, { size: 11, style: 'bold', color: GRAY_DARK, lineGap: 2 });
    this.y += 2;
  }

  /**
   * Render a label/value pair on a single row, with the label as a muted
   * eyebrow and the value emphasised. Long values wrap under the label.
   */
  field(label: string, value: string): void {
    this.text(label.toUpperCase(), { size: 7.5, style: 'bold', color: GRAY_LIGHT, lineGap: 2 });
    this.text(value || '—', { size: 10.5, style: 'bold', color: GRAY_DARK, lineGap: 3 });
    this.y += 6;
  }

  /** Render a bulleted list item with a hanging indent. */
  bullet(content: string): void {
    this.doc.setFont('helvetica', 'normal');
    this.doc.setFontSize(10);
    this.doc.setTextColor(GRAY_MID[0], GRAY_MID[1], GRAY_MID[2]);
    const indent = 14;
    const lines = this.doc.splitTextToSize(content, this.contentWidth - indent) as string[];
    const lineHeight = 14;
    lines.forEach((line, i) => {
      this.ensureSpace(lineHeight);
      if (i === 0) {
        this.doc.setTextColor(GRAY_LIGHT[0], GRAY_LIGHT[1], GRAY_LIGHT[2]);
        this.doc.text('•', this.marginX, this.y);
        this.doc.setTextColor(GRAY_MID[0], GRAY_MID[1], GRAY_MID[2]);
      }
      this.doc.text(line, this.marginX + indent, this.y);
      this.y += lineHeight;
    });
    this.y += 2;
  }

  /**
   * Draw the report title block: brand wordmark, report type and a teal rule.
   */
  titleBlock(productName: string, generatedOn: string): void {
    this.doc.setFont('helvetica', 'bold');
    this.doc.setFontSize(20);
    this.doc.setTextColor(TEAL[0], TEAL[1], TEAL[2]);
    this.doc.text('Classfi', this.marginX, this.y + 6);

    this.doc.setFont('helvetica', 'normal');
    this.doc.setFontSize(9);
    this.doc.setTextColor(GRAY_LIGHT[0], GRAY_LIGHT[1], GRAY_LIGHT[2]);
    this.doc.text('HTS Classification Report', this.pageWidth - this.marginX, this.y, {
      align: 'right',
    });
    this.doc.text(generatedOn, this.pageWidth - this.marginX, this.y + 12, { align: 'right' });

    this.y += 26;
    this.doc.setDrawColor(TEAL[0], TEAL[1], TEAL[2]);
    this.doc.setLineWidth(1.5);
    this.doc.line(this.marginX, this.y, this.pageWidth - this.marginX, this.y);
    this.y += 22;

    this.text(productName, { size: 15, style: 'bold', color: GRAY_DARK, lineGap: 3 });
    this.y += 8;
  }

  /**
   * Stamp a page footer (disclaimer + page numbers + optional id) on every page.
   * Call once after all content has been written.
   */
  finalizeFooter(classificationId?: string): void {
    const pageCount = this.doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i += 1) {
      this.doc.setPage(i);
      this.doc.setDrawColor(229, 229, 229);
      this.doc.setLineWidth(0.5);
      this.doc.line(
        this.marginX,
        this.pageHeight - this.marginBottom + 18,
        this.pageWidth - this.marginX,
        this.pageHeight - this.marginBottom + 18,
      );
      this.doc.setFont('helvetica', 'normal');
      this.doc.setFontSize(7.5);
      this.doc.setTextColor(GRAY_LIGHT[0], GRAY_LIGHT[1], GRAY_LIGHT[2]);
      const left = classificationId
        ? `Classification ID: ${classificationId}`
        : 'Generated by Classfi — informational only, not customs advice.';
      this.doc.text(left, this.marginX, this.pageHeight - this.marginBottom + 30);
      this.doc.text(
        `Page ${i} of ${pageCount}`,
        this.pageWidth - this.marginX,
        this.pageHeight - this.marginBottom + 30,
        { align: 'right' },
      );
    }
  }

  /** Trigger a browser download of the assembled document. */
  save(filename: string): void {
    this.doc.save(filename);
  }
}

/** Build the human-readable status label for a tariff addon program. */
const addonStatusLabel = (addon: TariffAddon): string => {
  if (addon.excluded) return 'Excluded';
  if (addon.applicable === true) return 'Applicable';
  if (addon.applicable === false) return 'N/A';
  return 'Uncertain';
};

/** Append the tariff-determination section to the writer when data is present. */
const writeTariffSection = (w: PdfWriter, tariff: TariffDeterminationData): void => {
  w.divider();
  w.heading('Tariff Determination');

  const base = tariff.base_mfn_rate?.rate_display || fmtPct(tariff.base_mfn_rate?.ad_valorem_pct ?? null);
  w.field('Base duty (MFN)', base);
  w.field('Additional', fmtPct(tariff.total_additional_pct));
  w.field('Total estimated rate', `${fmtPct(tariff.estimated_total_duty_pct)}${tariff.has_specific_components ? ' + per-unit' : ''}`);

  if (tariff.duty_owed !== null && tariff.duty_owed !== undefined) {
    const owed = Number(tariff.duty_owed).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    const on = tariff.customs_value
      ? ` on customs value USD ${Number(tariff.customs_value).toLocaleString()}`
      : '';
    w.field('Estimated duty owed', `≈ USD ${owed}${on}`);
  }

  if (tariff.stacking_order && tariff.stacking_order.length > 1) {
    w.text(`Stacking order: ${tariff.stacking_order.join(' → ')} (additive on customs value)`, {
      size: 9,
      color: GRAY_LIGHT,
    });
    w.spacer(6);
  }

  const addons = tariff.addons || {};
  const programLines = PROGRAM_ORDER.filter((key) => addons[key]);
  if (programLines.length > 0) {
    w.text('Additional tariff programs', { size: 9.5, style: 'bold', color: GRAY_DARK });
    w.spacer(2);
    programLines.forEach((key) => {
      const addon = addons[key];
      const parts = [
        `${PROGRAM_LABELS[key]} — ${addonStatusLabel(addon)}`,
        addon.rate_display,
        addon.basis || addon.reason || '',
      ].filter(Boolean);
      w.bullet(parts.join(': '));
    });
  }

  if (tariff.flags && tariff.flags.length > 0) {
    w.spacer(4);
    w.text('Flags', { size: 9.5, style: 'bold', color: GRAY_DARK });
    w.spacer(2);
    tariff.flags.forEach((flag) => w.bullet(`[${flag.severity}] ${flag.message}`));
  }

  if (tariff.warnings && tariff.warnings.length > 0) {
    w.spacer(4);
    w.text('Notes & warnings', { size: 9.5, style: 'bold', color: GRAY_DARK });
    w.spacer(2);
    tariff.warnings.forEach((warning) => w.bullet(warning));
  }

  w.spacer(4);
  w.text(
    tariff.informational_only ||
      'Duty rates are informational only and must not influence classification choice.',
    { size: 8, style: 'italic', color: GRAY_LIGHT },
  );
};

/**
 * Generate and download a PDF report for an HTS classification result, including
 * the embedded tariff determination, alternative codes, rationale, assumptions
 * and sources. Produces selectable, vector text (not a screenshot).
 */
export const exportClassificationPdf = (
  data: HTSClassificationData,
  meta: ClassificationPdfMeta,
  tariff?: TariffDeterminationData,
): void => {
  const w = new PdfWriter();
  const generatedOn = new Date().toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });

  w.titleBlock(meta.productName, generatedOn);

  // ── Summary ──────────────────────────────────────────────────────────────
  w.field('Product', data.product_category || data.hts_description || 'Product Classification');
  w.field('HTS Code', data.primary_hts_code);
  w.field('Country of Origin', meta.countryOfOrigin || data.country_of_origin || '—');

  const confidence = confidenceRange(data.confidence_score);
  if (confidence) {
    w.field('Confidence', `${confidence}`);
  }

  if (!tariff) {
    const baseDuty = data.hts_duty_rate || data.duty_rate || data.base_duty;
    if (baseDuty) w.field('Base Duty', baseDuty);
  }

  if (data.additional_tariffs_found && data.additional_tariffs_found.length > 0) {
    w.text('ADDITIONAL TARIFFS', { size: 7.5, style: 'bold', color: GRAY_LIGHT, lineGap: 2 });
    w.spacer(2);
    data.additional_tariffs_found.forEach((t) => {
      const label = t.tariff_type ? `${t.tariff_type}: ` : '';
      w.bullet(`${label}${t.rate || t.description || '—'}`);
    });
    w.spacer(4);
  }

  // ── Tariff determination ───────────────────────────────────────────────────
  if (tariff) {
    writeTariffSection(w, tariff);
  }

  // ── Alternative codes ──────────────────────────────────────────────────────
  if (data.additional_classifications && data.additional_classifications.length > 0) {
    w.divider();
    w.heading('Alternative codes');
    data.additional_classifications.slice(0, 5).forEach((alt) => {
      const conf = `${(alt.confidence * 100).toFixed(0)}% confidence`;
      w.text(`${alt.hts_code}  ·  ${conf}`, { size: 10, style: 'bold', color: GRAY_DARK, lineGap: 2 });
      if (alt.description) w.text(alt.description, { size: 9.5, color: GRAY_MID, lineGap: 3 });
      if (alt.reason_rejected) {
        w.text(`Why not selected: ${alt.reason_rejected}`, { size: 9, color: GRAY_LIGHT, lineGap: 3 });
      }
      w.spacer(6);
    });
  }

  // ── Why this code ──────────────────────────────────────────────────────────
  const hasRationale = data.notes?.length || data.hts_description || data.chapter_description;
  if (hasRationale) {
    w.divider();
    w.heading('Why this code');
    if (data.hts_description) w.bullet(`Classified under "${data.hts_description}"`);
    if (data.chapter_description) w.bullet(`Chapter ${data.chapter}: ${data.chapter_description}`);
    (data.notes || []).forEach((note) => w.bullet(note));
  }

  // ── Assumptions ────────────────────────────────────────────────────────────
  if (data.assumptions && data.assumptions.length > 0) {
    w.divider();
    w.heading('Assumptions');
    data.assumptions.forEach((assumption) => w.bullet(assumption));
  }

  // ── Sources ────────────────────────────────────────────────────────────────
  const sources =
    data.source_references && data.source_references.length > 0
      ? data.source_references.map((ref) => (ref.url ? `${ref.label} (${ref.url})` : ref.label))
      : ['USHTS', 'AI Classification', data.chapter ? `Ch.${data.chapter}` : null].filter(
          Boolean,
        ) as string[];
  if (sources.length > 0) {
    w.divider();
    w.heading('Sources');
    w.text(sources.join('  •  '), { size: 9, color: GRAY_MID });
  }

  w.finalizeFooter(meta.classificationId);

  const safeName = meta.productName.replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '') || 'classification';
  w.save(`${safeName}_HTS_Classification.pdf`);
};
